import { useParams } from 'react-router-dom';
import { LoanOption, LoanOptionOverride } from '../shared/models';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../shared/components';
import { FormikUtils, NumberUtils } from '../shared/utils';
import { Field, Formik, FormikErrors } from 'formik';
import { useApi } from '../shared/context';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { getApiResponseData } from '../shared/utils/axios';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { QueryKeys } from '../shared/hooks';
import { Grid } from '@mui/material';

interface LoanOptionOverrideFormProps {
	option: LoanOption | null;
	close: () => void;
}

export const LoanOptionOverrideForm: React.FC<LoanOptionOverrideFormProps> = ({ option, close }) => {
	const { guid } = useParams<'guid'>();

	const api = useApi();
	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const loanOptionMutation = useMutation(
		async ({ values }: OptionOverrideMutationVariables) => {
			const dto = {
				capacityOverride: NumberUtils.ParseDecimal(values.capacityOverride, undefined),
				lenderMatchCountOverride: NumberUtils.ParseDecimal(values.lenderMatchCountOverride, undefined),
				removeOverride: values.removeOverride,
			};

			const { data, error } = await getApiResponseData<LoanOptionOverride>(
				api.post(CerebroApiEndpoints.Matching.LoanOptionOverride(guid, option?.guid), dto)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QueryKeys.Matching.Get(guid));
				snackbar.enqueueSnackbar('Successfully updated the loan option config.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
				close();
			},
		}
	);

	return (
		<CerebroDrawer open={option != null} onClose={close} PaperProps={{ sx: { maxWidth: { md: 700 } } }}>
			<CerebroHeading>Edit Loan Option Config</CerebroHeading>

			<Formik
				initialValues={{
					lenderMatchCountOverride: NumberUtils.FormatDecimal(option?.matchedLenders ?? ''),
					capacityOverride: NumberUtils.FormatFloat(option?.highLoanAmount ?? '', 2),
					removeOverride: false,
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(loanOptionMutation.mutate)}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit }) => (
					<CerebroForm onSubmit={handleSubmit}>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							{option?.isOverridden && (
								<Grid item xs={12}>
									<CerebroLabel id="removeOverride">Remove the overridden value?</CerebroLabel>
									<Field
										id="removeOverride"
										as={CerebroYesNo}
										name="removeOverride"
										disabled={isSubmitting}
									/>
								</Grid>
							)}

							{!values.removeOverride && (
								<>
									<Grid item xs={12}>
										<CerebroLabel id="lenderMatchCountOverride">Matched Lenders</CerebroLabel>
										<Field
											id="lenderMatchCountOverride"
											as={CerebroMaskedInput}
											name="lenderMatchCountOverride"
											disabled={isSubmitting}
										/>
										{FormikUtils.IsInvalid('lenderMatchCountOverride', errors, touched) && (
											<CerebroText variant="error">{errors.lenderMatchCountOverride}</CerebroText>
										)}
									</Grid>

									<Grid item xs={12}>
										<CerebroLabel id="capacityOverride">Loan Capacity</CerebroLabel>
										<Field
											id="capacityOverride"
											as={CerebroMaskedInput}
											name="capacityOverride"
											disabled={isSubmitting}
										/>
										{FormikUtils.IsInvalid('capacityOverride', errors, touched) && (
											<CerebroText variant="error">{errors.capacityOverride}</CerebroText>
										)}
									</Grid>
								</>
							)}
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

type OptionOverrideErrors = FormikErrors<OptionOverrideValues>;
type OptionOverrideMutationVariables = FormikUtils.MutateOnSubmitVariables<OptionOverrideValues>;
interface OptionOverrideValues {
	lenderMatchCountOverride: string;
	capacityOverride: string;
	removeOverride: boolean;
}

function validateForm(values: OptionOverrideValues) {
	const errors: OptionOverrideErrors = {};

	if (!values.capacityOverride) {
		errors.capacityOverride = 'You must enter a valid capacity.';
	}

	if (!values.lenderMatchCountOverride) {
		errors.lenderMatchCountOverride = 'You must enter the number of matched lenders.';
	}

	return errors;
}
